import type {
  HotelPaymentSelection,
  PrepareHotelCheckoutFlowSpec,
  SubmitHotelBookingFlowSpec,
} from '@elara/domain/flows';
import type { HotelModelSpec } from '@elara/domain/models/HotelModelSpec';
import type { RoomOptionModelSpec } from '@elara/domain/models/RoomOptionModelSpec';
import type { FileAttachment } from '@elara/domain/models/schemas/attachment-schemas';
import type {
  BookingAddress,
  HotelBookingRoom,
  LeadGuest,
  Occupancy,
} from '@elara/domain/models/schemas/booking-schemas';

import type { ItineraryItemModelSpec } from '@elara/domain/models/ItineraryItemModelSpec';

import type { HotelsContainer } from '#containers/HotelsContainer.js';
import type { ItineraryContainer } from '#containers/ItineraryContainer.js';
import type { RoomOptionsContainer } from '#containers/RoomOptionsContainer.js';
import type { ClientDocumentHandle } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { Hotel } from '#models/HotelModel.js';
import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { RoomOptionModel as RoomOption } from '#models/RoomOptionModel.js';
import type { RoomRateModel } from '#models/RoomRateModel.js';

import { trackBeginCheckout } from '#lib/analytics-events.js';
import { partlyBookedDraft, roomCountDraft, unavailableRoomsDraft } from '#lib/ask-elara-drafts.js';
import { bookingOccupancyToRateOccupancy } from '#lib/occupancy-utils.js';
import { createState } from '#lib/signals-utils.js';

type PrepData = PrepareHotelCheckoutFlowSpec['OutputType'];
type PriceCheckResult = NonNullable<PrepData['priceCheckResult']>[number];

/**
 * A room whose price moved between what the traveler saw and the server's latest re-price. The room
 * stays bookable at the new figure; `previousTotal` / `newTotal` are what the page shows as old → new.
 */
export interface RoomPriceChange {
  itineraryItemId: string;
  previousTotal?: NonNullable<PriceCheckResult['previousTotal']>;
  newTotal?: NonNullable<PriceCheckResult['newTotal']>;
}

/**
 * What the traveler is told when a submit found the price moved but the in-place re-prepare could not load
 * the new total. Exported so both apps' tests can assert the copy their error slot shows. It replaces the
 * price-change notice, which would otherwise claim a new total over the old one.
 */
export const REPRICE_FAILED_MESSAGE = "The price changed and we couldn't load the new total. Please try again.";

/** Only a price movement keeps the room in the checkout; every other result code means the rate is gone. */
function isPriceChange(result: PriceCheckResult): boolean {
  return result.code === 'priceChanged';
}

function toPriceChange(result: PriceCheckResult): RoomPriceChange {
  return {
    itineraryItemId: result.itineraryItemId,
    ...(result.previousTotal ? { previousTotal: result.previousTotal } : {}),
    ...(result.newTotal ? { newTotal: result.newTotal } : {}),
  };
}

type SubmitOutput = SubmitHotelBookingFlowSpec['OutputType'];

/** Why a room was dropped from the checkout, per itinerary item — drives the notice's wording. */
export interface DroppedRoomReason {
  code: PriceCheckResult['code'];
  /** The supplier's stated "rooms left" when the room was dropped for count (`insufficientInventory`). */
  availableQuantity?: number;
}

/**
 * A submit that booked some rooms and not others. There is no rollback (ELARA-1079): the booked rooms are
 * on the trip, so the checkout is over and the page shows this as the outcome, not as an error.
 */
export type PartialFailureReason = 'unavailable' | 'priceChanged' | 'unconfirmed' | 'failed';
const PARTIAL_FAILURE_REASONS: ReadonlySet<string> = new Set<PartialFailureReason>([
  'unavailable',
  'priceChanged',
  'unconfirmed',
  'failed',
]);

export interface PartialBookingOutcome {
  /** The server's sentence: what is booked, which room did not book, and the next step. */
  message: string;
  bookedItemIds: string[];
  failedItemId?: string;
  /**
   * Why the room did not book. Only `unavailable` calls for an alternative; `unconfirmed` means the create
   * may have gone through and must not be retried. Absent from older servers, read as unavailable.
   */
  failedReason?: PartialFailureReason;
}

/**
 * The one room type every dropped room belongs to, with the count the hotel stated, when every room was
 * dropped for count (`insufficientInventory`). Undefined for any other reason or a mix of room types.
 */
function countDropOf(
  dropped: HotelBookingRoom[],
  reasons: Record<string, DroppedRoomReason>,
  roomsById: Map<string, RoomOption | undefined>,
): { name: string; available: number } | undefined {
  if (dropped.length === 0) return;
  const counts = dropped.map((room) => {
    const reason = reasons[room.itineraryItemId];
    return reason?.code === 'insufficientInventory' ? reason.availableQuantity : undefined;
  });
  if (!counts.every((c): c is number => typeof c === 'number')) return;
  const names = new Set(dropped.map((room) => roomsById.get(room.roomOptionId)?.displayName ?? 'Room'));
  if (names.size !== 1) return;
  const available = Math.min(...counts);
  // A stated count of none is a sold-out rate, not "only 0 are available".
  if (available < 1) return;
  return { name: [...names][0]!, available };
}

/** The partial outcome a rejected submit carries in its details, or undefined for any other failure. */
function partialOutcomeFrom(err: unknown): PartialBookingOutcome | undefined {
  const { message, details } = (err ?? {}) as { message?: unknown; details?: unknown };
  const d = (details ?? {}) as { bookedItemIds?: unknown; failedItemId?: unknown; failedReason?: unknown };
  if (!Array.isArray(d.bookedItemIds) || d.bookedItemIds.length === 0) return;
  const bookedItemIds = d.bookedItemIds.filter((id): id is string => typeof id === 'string');
  if (bookedItemIds.length === 0) return;
  const failedReason =
    typeof d.failedReason === 'string' && PARTIAL_FAILURE_REASONS.has(d.failedReason)
      ? (d.failedReason as PartialFailureReason)
      : undefined;
  return {
    message: typeof message === 'string' ? message : 'Some rooms were booked and one was not.',
    bookedItemIds,
    ...(typeof d.failedItemId === 'string' ? { failedItemId: d.failedItemId } : {}),
    ...(failedReason ? { failedReason } : {}),
  };
}

type BookingDetails = NonNullable<ItineraryItem['booking']>;

/**
 * One booked room's confirmation, as the post-booking screen shows it.
 *
 * Sabre books one room per call, so each room in a multi-room checkout comes back with its OWN locator —
 * there is no single confirmation number for the group. Hence one entry per itinerary item rather than a
 * trip-level code.
 */
export interface BookedRoomConfirmation {
  itineraryItemId: string;
  roomName: string | undefined;
  /** The room's lead photo, for the confirmation list's thumbnail. */
  thumbnailUrl: string | undefined;
  /** What the room was priced for — the occupancy fact, and the fallback when the rate can't be resolved. */
  occupancy: Occupancy;
  /**
   * The rate behind the room, found by `(productCode, occupancy)` — the identity lookup, because
   * `selectedRateKey` rotates on every fresh Sabre search. Carries the rate name and the cancellation
   * headline the guest agreed to, plus the full terms for the policy sheet.
   */
  rate: RoomRateModel | undefined;
  /**
   * What to show the guest: the property's own confirmation number when Sabre returned one, else the PNR
   * locator (`booking.reference`). Undefined while the write is still in flight, and on a dry-run backend,
   * which records the booking without creating a real PNR.
   */
  code: string | undefined;
  status: BookingDetails['status'] | undefined;
}

/**
 * Reactive state for the hotel checkout page.
 *
 * Single-mode (prep) — kicks off `PrepareHotelCheckout`, holds the priced room
 * set in memory, and submits via `SubmitHotelBooking`. The per-item state lives
 * on each itinerary item's `booking` field; the bookings tab is where a
 * completed booking is inspected and amended.
 *
 * Hotel + per-room option Firestore handles are side-loaded so the right-rail
 * summary can show the hotel name / room thumbnails, and the checked-out
 * itinerary items alongside them so a submit's confirmation codes land
 * reactively — see {@link BookedRoomConfirmation}. Mobile renders those as its
 * post-booking confirmation screen; web still returns to the bookings tab.
 */
export function createHotelCheckoutState(
  flows: AppFlowsContainer,
  hotels: HotelsContainer,
  roomOptions: RoomOptionsContainer,
  itinerary: ItineraryContainer,
) {
  return createState({
    prepTripId: undefined as string | undefined,
    prepItemIds: [] as string[],
    prepData: undefined as PrepData | undefined,
    prepError: undefined as string | undefined,
    prepStatus: 'idle' as 'idle' | 'preparing' | 'ready' | 'error',
    /**
     * Rooms that came back unavailable from PriceCheck and have been dropped
     * from `prepData`. Surfaced as a banner in the right-rail summary so the
     * user knows what was excluded; never submitted.
     */
    droppedRooms: [] as HotelBookingRoom[],
    /** Why each room in `droppedRooms` was dropped, by itinerary item id. */
    droppedRoomReasons: {} as Record<string, DroppedRoomReason>,
    /** Set when a submit booked some rooms and not others; the page shows it in place of the form. */
    partialOutcome: undefined as PartialBookingOutcome | undefined,
    /**
     * Rooms whose price moved since the traveler last saw it — at prepare (against the saved rate) or at
     * submit (against the agreed snapshot). They stay in `prepData` at the NEW price; the page shows old →
     * new and the next submit books at the new figure. Cleared by `reset`.
     */
    priceChanges: [] as RoomPriceChange[],

    hotelHandle: undefined as ClientDocumentHandle<HotelModelSpec> | undefined,
    /**
     * The `hotelId` {@link hotelHandle} was opened for. A trip can hold rooms at more than one property, so
     * the handle has to be re-pointed when a later prepare resolves a different hotel — guarding only on
     * "is there a handle?" kept the first hotel's name above the second hotel's rooms.
     */
    hotelHandleId: undefined as string | undefined,
    roomHandlesById: {} as Record<string, ClientDocumentHandle<RoomOptionModelSpec> | undefined>,
    /**
     * The itinerary items being checked out, keyed by item id. Opened by `hydrateRelated` while the guest
     * is still filling the form — so a submit's `booking` writes land on listeners that are ALREADY live
     * and the confirmation renders without a second round trip.
     */
    itemHandlesById: {} as Record<string, ClientDocumentHandle<ItineraryItemModelSpec> | undefined>,
    isSubmitting: false,
    submitError: undefined as string | undefined,

    get isReady(): boolean {
      return this.prepStatus === 'ready' && !!this.prepData;
    },

    get hotel(): Hotel | undefined {
      return this.hotelHandle?.data as Hotel | undefined;
    },

    get roomOptionsById(): Map<string, RoomOption | undefined> {
      const out = new Map<string, RoomOption | undefined>();
      for (const [id, handle] of Object.entries(this.roomHandlesById)) {
        out.set(id, handle?.data as RoomOption | undefined);
      }
      return out;
    },

    /**
     * The dropped-rooms sentence when every dropped room went for a stated count — "Only 1 Conrad Suite is
     * available. We've removed the other from your booking…". Undefined for any other reason, or a mix of
     * room types, so the page keeps its plain "no longer available" sentence.
     */
    get droppedRoomsNote(): string | undefined {
      const dropped = this.droppedRooms;
      const count = countDropOf(dropped, this.droppedRoomReasons, this.roomOptionsById);
      if (!count) return;
      const only = count.available === 1 ? `Only 1 ${count.name} is` : `Only ${count.available} ${count.name}s are`;
      const removed = dropped.length === 1 ? 'the other' : `the other ${dropped.length}`;
      return `${only} available. We've removed ${removed} from your booking. Ask Elara to find alternatives.`;
    },

    /**
     * The chat draft behind the dropped-rooms notice's "Ask Elara": the rooms, the property and the stay,
     * worded by count when the hotel stated fewer of the type than asked. Undefined when nothing was dropped.
     */
    get droppedRoomsDraft(): string | undefined {
      const dropped = this.droppedRooms;
      const prep = this.prepData;
      if (dropped.length === 0 || !prep) return;
      const roomsById = this.roomOptionsById;
      const stay = { hotelName: this.hotel?.name, checkInDate: prep.checkInDate, checkOutDate: prep.checkOutDate };
      const count = countDropOf(dropped, this.droppedRoomReasons, roomsById);
      if (count)
        return roomCountDraft({ ...stay, roomName: count.name, available: count.available, dropped: dropped.length });
      return unavailableRoomsDraft({
        ...stay,
        roomNames: dropped.map((room) => roomsById.get(room.roomOptionId)?.displayName ?? 'Room'),
      });
    },

    /**
     * The chat draft behind the partly-booked outcome's "Ask Elara". Undefined unless a submit ended partly
     * booked with a room that is gone: a price change is reopened instead, and an unconfirmed create must not
     * be worked around by booking another room.
     */
    get partialOutcomeDraft(): string | undefined {
      const outcome = this.partialOutcome;
      const prep = this.prepData;
      if (!outcome || !prep) return;
      if (outcome.failedReason && outcome.failedReason !== 'unavailable') return;
      const roomsById = this.roomOptionsById;
      const booked = new Set(outcome.bookedItemIds);
      const nameOf = (room: HotelBookingRoom) => roomsById.get(room.roomOptionId)?.displayName ?? 'Room';
      return partlyBookedDraft({
        hotelName: this.hotel?.name,
        checkInDate: prep.checkInDate,
        checkOutDate: prep.checkOutDate,
        bookedNames: prep.rooms.filter((room) => booked.has(room.itineraryItemId)).map(nameOf),
        notBookedNames: prep.rooms.filter((room) => !booked.has(room.itineraryItemId)).map(nameOf),
      });
    },

    /**
     * Per-room confirmations for the rooms this checkout submitted, in the same order as `prepData.rooms`.
     * Reads through the live item handles, so codes appear as the server's writes arrive.
     *
     * Empty before `hydrateRelated` runs. An entry with no `code` is a room whose locator hasn't landed
     * (or was never created, on a dry-run backend) — callers should render that as pending rather than as
     * a failure.
     */
    get bookedRooms(): BookedRoomConfirmation[] {
      const roomsById = this.roomOptionsById;
      // After a partial order only the rooms that booked have a confirmation to show.
      const booked = this.partialOutcome ? new Set(this.partialOutcome.bookedItemIds) : undefined;
      const rooms = (this.prepData?.rooms ?? []).filter((room) => !booked || booked.has(room.itineraryItemId));
      return rooms.map((room) => {
        const item = this.itemHandlesById[room.itineraryItemId]?.data as ItineraryItem | undefined;
        const booking = item?.booking;
        const roomOption = roomsById.get(room.roomOptionId);
        return {
          itineraryItemId: room.itineraryItemId,
          roomName: roomOption?.displayName,
          thumbnailUrl: roomOption?.images?.[0]?.url,
          occupancy: room.occupancy,
          rate: roomOption?.getRateByIdentity({
            productCode: room.productCode,
            occupancy: bookingOccupancyToRateOccupancy(room.occupancy),
          }),
          code: booking?.confirmationNumber ?? booking?.reference,
          status: booking?.status,
        };
      });
    },

    /**
     * Run `PrepareHotelCheckout` to get a priced room set the user can edit.
     * Re-runnable: calling again with the same args refreshes prices and
     * resets transient `prepData` state. Updates `prepStatus` so the page can
     * show a skeleton while in flight.
     *
     * Any rooms that came back unavailable are split off into `droppedRooms`
     * so they don't enter the form or the eventual SubmitHotelBooking call —
     * the page surfaces them as a banner in the summary.
     */
    async prepare(tripId: string, itineraryItemIds: string[]): Promise<void> {
      this.reset();
      this.prepTripId = tripId;
      this.prepItemIds = itineraryItemIds;
      this.prepStatus = 'preparing';
      this.prepError = undefined;
      try {
        const result = await flows.prepareHotelCheckout({ tripId, itineraryItemIds });
        this.applyPrepResponse(result.data);
        this.prepStatus = 'ready';
        trackBeginCheckout({ itemCategory: 'hotel', tripId });
      } catch (err) {
        this.prepError = err instanceof Error ? err.message : String(err);
        this.prepStatus = 'error';
        throw err;
      }
    },

    /**
     * Splits a `PrepareHotelCheckout` response (or a `submit`'s
     * `price_changed` payload re-shaped into one) into available rooms that
     * land in `prepData` and unavailable rooms that land in `droppedRooms`.
     */
    applyPrepResponse(data: PrepData): void {
      const results = data.priceCheckResult ?? [];
      // A price movement is not a lost room: the server already re-priced it and built its snapshot from
      // the new figure, so it stays bookable. Every other code means the rate is gone.
      const unavailable = new Set(results.filter((r) => !isPriceChange(r)).map((r) => r.itineraryItemId));
      const availableRooms = data.rooms.filter((r) => !unavailable.has(r.itineraryItemId));
      const droppedRooms = data.rooms.filter((r) => unavailable.has(r.itineraryItemId));
      this.prepData = {
        idempotencyKey: data.idempotencyKey,
        hotelId: data.hotelId,
        checkInDate: data.checkInDate,
        checkOutDate: data.checkOutDate,
        rooms: availableRooms,
        priceSnapshot: data.priceSnapshot.filter((s) => !unavailable.has(s.itineraryItemId)),
        // Carried through verbatim — lookups are keyed by itinerary item id, so
        // entries for dropped rooms are simply never read.
        eligibleExtras: data.eligibleExtras,
      };
      this.droppedRooms = droppedRooms;
      const reasons: Record<string, DroppedRoomReason> = {};
      for (const r of results) {
        if (isPriceChange(r)) continue;
        reasons[r.itineraryItemId] = {
          code: r.code,
          ...(typeof r.availableQuantity === 'number' ? { availableQuantity: r.availableQuantity } : {}),
        };
      }
      this.droppedRoomReasons = reasons;
      this.priceChanges = results
        .filter((r) => isPriceChange(r) && !unavailable.has(r.itineraryItemId))
        .map(toPriceChange);
    },

    /**
     * Re-runs `PrepareHotelCheckout` for the rooms still in the checkout WITHOUT resetting — the traveler's
     * form, card choice and place on the page survive. Used after a submit reported a price movement: the
     * fresh prepare carries the new snapshot, so the next submit books at the figure the page now shows.
     * `changes` (from the submit) win over anything the fresh prepare reports, since they hold the
     * old → new pair the traveler needs to see; rooms the submit already dropped stay dropped.
     *
     * Throws {@link REPRICE_FAILED_MESSAGE} when the new figure cannot be loaded, WITHOUT setting
     * `priceChanges`: the price-change notice says "the total now shows the new price", which would be false
     * over the old total. The refused snapshot stays, so the next submit re-detects the drift instead of
     * booking blind; `submit` turns the throw into `submitError` for both apps' error slot.
     */
    async repriceInPlace(changes: RoomPriceChange[]): Promise<void> {
      const tripId = this.prepTripId;
      const itineraryItemIds = (this.prepData?.rooms ?? []).map((r) => r.itineraryItemId);
      // Nothing to re-prepare against, so no new total either — the same failure as a re-prepare that threw.
      if (!tripId || itineraryItemIds.length === 0) throw new Error(REPRICE_FAILED_MESSAGE);
      const alreadyDropped = this.droppedRooms;
      const alreadyDroppedReasons = this.droppedRoomReasons;
      try {
        const fresh = await flows.prepareHotelCheckout({ tripId, itineraryItemIds });
        this.applyPrepResponse(fresh.data);
      } catch (err) {
        throw new Error(REPRICE_FAILED_MESSAGE, { cause: err });
      }
      const droppedIds = new Set(alreadyDropped.map((r) => r.itineraryItemId));
      this.droppedRooms = [...alreadyDropped, ...this.droppedRooms.filter((r) => !droppedIds.has(r.itineraryItemId))];
      this.droppedRoomReasons = { ...alreadyDroppedReasons, ...this.droppedRoomReasons };
      this.priceChanges = changes;
    },

    /**
     * Submit the form. Forwards prep state + the user's form input. On
     * `price_changed`, refreshes `prepData` in place so the page can
     * re-render against new prices — and rejects (with `submitError` set)
     * when that refresh fails, so neither app claims a new total it never
     * loaded. On `submitted`, returns the shared `bookingGroupId` so the
     * caller can navigate.
     */
    async submit(form: {
      leadGuest: LeadGuest;
      address: BookingAddress;
      customerRequest?: string;
      attachments?: FileAttachment[];
      rooms: HotelBookingRoom[];
      payment: HotelPaymentSelection;
    }): Promise<SubmitOutput> {
      const tripId = this.prepTripId;
      const prep = this.prepData;
      if (!tripId || !prep) {
        throw new Error('Checkout has not been prepared yet.');
      }
      this.isSubmitting = true;
      this.submitError = undefined;
      try {
        // itineraryItemIds + priceSnapshot come from `form.rooms` so any
        // dropped (unavailable) rooms are excluded from the submission —
        // the user only books what they actually saw in the form.
        const itineraryItemIds = form.rooms.map((r) => r.itineraryItemId);
        const itemIdSet = new Set(itineraryItemIds);
        const priceSnapshot = prep.priceSnapshot.filter((s) => itemIdSet.has(s.itineraryItemId));
        const result = await flows.submitHotelBooking({
          tripId,
          idempotencyKey: prep.idempotencyKey,
          itineraryItemIds,
          hotelId: prep.hotelId,
          checkInDate: prep.checkInDate,
          checkOutDate: prep.checkOutDate,
          leadGuest: form.leadGuest,
          address: form.address,
          customerRequest: form.customerRequest,
          attachments: form.attachments,
          rooms: form.rooms,
          priceSnapshot,
          payment: form.payment,
        });
        if (result.data.status === 'price_changed') {
          const results = result.data.priceCheckResult;
          const gone = results.filter((r) => !isPriceChange(r));
          const moved = results.filter((r) => isPriceChange(r));
          // Rooms whose rate vanished drop as before. Rooms whose price merely moved stay — but the snapshot
          // the page holds is the one the server just refused, so re-prepare in place to pick up the new
          // figure, keeping the submit's old → new for the banner.
          this.applyPrepResponse({ ...prep, rooms: result.data.rooms, priceCheckResult: gone });
          if (moved.length > 0) await this.repriceInPlace(moved.map(toPriceChange));
        }
        return result.data;
      } catch (err) {
        const partial = partialOutcomeFrom(err);
        if (partial) {
          // Some rooms booked and one did not: the booked rooms are on the trip and nothing is left to pay
          // for here, so this resolves the way a full success does and the page shows the outcome.
          this.partialOutcome = partial;
          return { status: 'submitted' as const, bookingGroupId: prep.idempotencyKey };
        }
        this.submitError = err instanceof Error ? err.message : 'Failed to submit booking';
        throw err;
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * Side-load hotel + per-room option Firestore handles for the summary, plus the itinerary items
     * being booked so their `booking` (and its confirmation code) lands reactively after submit.
     *
     * Document `get`s rather than a query — no composite index, and the item ids are already known.
     */
    hydrateRelated(tripId: string): void {
      const hotelId = this.prepData?.hotelId;
      const rooms = [...(this.prepData?.rooms ?? []), ...this.droppedRooms];
      if (hotelId && this.hotelHandleId !== hotelId) {
        this.hotelHandle?.unsubscribe?.();
        this.hotelHandle = hotels.ClientHotel.get({ id: hotelId });
        this.hotelHandleId = hotelId;
      }
      const next: Record<string, ClientDocumentHandle<RoomOptionModelSpec> | undefined> = { ...this.roomHandlesById };
      for (const room of rooms) {
        if (!next[room.roomOptionId]) {
          next[room.roomOptionId] = roomOptions.ClientRoomOption.get({ tripId, id: room.roomOptionId });
        }
      }
      this.roomHandlesById = next;

      // Only the rooms actually being submitted — a dropped room is never booked, so it has no
      // confirmation to wait for.
      const nextItems: Record<string, ClientDocumentHandle<ItineraryItemModelSpec> | undefined> = {
        ...this.itemHandlesById,
      };
      for (const room of this.prepData?.rooms ?? []) {
        if (!nextItems[room.itineraryItemId]) {
          nextItems[room.itineraryItemId] = itinerary.ClientItineraryItem.get({ tripId, id: room.itineraryItemId });
        }
      }
      this.itemHandlesById = nextItems;
    },

    reset(): void {
      this.hotelHandle?.unsubscribe?.();
      for (const handle of Object.values(this.roomHandlesById)) handle?.unsubscribe?.();
      for (const handle of Object.values(this.itemHandlesById)) handle?.unsubscribe?.();
      this.hotelHandle = undefined;
      this.hotelHandleId = undefined;
      this.roomHandlesById = {};
      this.itemHandlesById = {};
      this.prepTripId = undefined;
      this.prepItemIds = [];
      this.prepData = undefined;
      this.prepError = undefined;
      this.prepStatus = 'idle';
      this.droppedRooms = [];
      this.droppedRoomReasons = {};
      this.partialOutcome = undefined;
      this.priceChanges = [];
      this.submitError = undefined;
    },
  });
}

export type HotelCheckoutState = ReturnType<typeof createHotelCheckoutState>;
